import React, {memo, useContext, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {getTabSessionId} from '@/app/helpers/getTabSessionId';
import {RenderConditionContext} from '@/app/contexts/RenderConditionContext';
import {sendStats} from '@/common/helpers/stats/sendStats';
import {generateRandomSafeBase64} from '@/common/helpers/utils/generateRandomBase64';
import {useAdRefresh} from '@/app/components/common/advertising';
import {useBannerViewability} from '@/app/components/common/advertising/useBannerViewability';
import styles from './AdpartnerAdBanner.css';

interface AdpartnerAdBannerProps {
	className?: string;
	unitId: string;
	width: number;
	height: number;
	analyticsLabel: string;
	interfaceName: string;
	placeholder?: boolean;
}

export const AdpartnerAdBanner = memo<AdpartnerAdBannerProps>(props => {
	const {className, unitId, width, height, analyticsLabel, interfaceName, placeholder} = props;

	const [placeholderShown, setPlaceholderShown] = useState(!!placeholder);
	const [showIndex, setShowIndex] = useState<number>(0);
	const renderConditionRef = useContext(RenderConditionContext);
	const isInitialRender = useMemo(() => renderConditionRef.current.isInitial, []);

	const rootRef = useRef<HTMLDivElement>(null);
	const isViewableEnoughRef = useBannerViewability(rootRef, interfaceName, [showIndex]);

	// Inject script on mount and the showIndex change (when banner should be refreshed)
	useEffect(() => {
		const head = document.querySelector('head')!;
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.async = true;
		script.src = `//a4p.adpartner.pro/vunit?id=${unitId}&${Math.random()}`;
		head.appendChild(script);

		return () => {
			script.remove();
		};
	}, [unitId, showIndex]);

	useAdRefresh(() => {
		if (!isViewableEnoughRef.current) {
			return;
		}

		setShowIndex(currentValue => currentValue + 1);
	});

	useEffect(() => {
		sendStats(`ad/${analyticsLabel}/display/ap/${unitId}`);
		const loadingId = generateRandomSafeBase64(16);
		const tabId = getTabSessionId();
		const impressionIndex = isInitialRender && showIndex === 0 ? 0 : showIndex + 1;
		sendStats(
			`comm/v1/${Date.now()}/display/${loadingId}/${tabId}/${interfaceName}/${impressionIndex}/ap/${analyticsLabel}/${unitId}`,
		);

		const mutationObserver = new MutationObserver(() => {
			const rootNode = rootRef.current!;

			const status = rootNode.clientHeight === 0 ? 'empty' : 'filled';
			sendStats(`ad/${analyticsLabel}/shown/${status}/ap/${unitId}`);
			sendStats(`comm/v1/${Date.now()}/shown/${loadingId}/${status}`);

			if (placeholderShown) {
				setPlaceholderShown(false);
			}

			mutationObserver.disconnect();
		});

		mutationObserver.observe(rootRef.current!, {
			childList: true,
		});

		return () => {
			mutationObserver.disconnect();
		};
	}, [showIndex]);

	return (
		<div
			key={showIndex}
			ref={rootRef}
			id={`adpartner-vunit-${unitId}`}
			style={{width, height}}
			className={cn(styles.root, placeholderShown && styles.withPlaceholder, className)}
		/>
	);
});

AdpartnerAdBanner.displayName = 'AdpartnerAdBanner';
